import React, { Component } from "react";
import { Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import Select from "react-select";

import { translate } from "@/components/IntlProviderWrapper";
import { getBackend } from "@/lib/backend/Backend2";
import * as t from "@/lib/backend/manufacturing2.generated.types";
import * as Bl from "@/lib/BusinessLogic";
import { Toolbar, ToolbarItem } from "@/components/Toolbar";
import { classNames, deepClone } from "@/lib/Util";

import { IGNORE_COMPETENCE_FLAG } from "@/components/manufacturing/ShiftAssignmentDetails";
import { LINE_TAGS } from "@/lib/ManufacturingTags.generated";

import "./PeopleCompetenceModal.scss";

export type SingleCompetence = {
    title: string;
    uuid: string;
    group_uuid: string;
    level: number;
    line_group_title: string;
    weight: number;
};

export type SingleCompetenceGroup = {
    title: string;
    uuid: string;
};

export type Props = {
    show: boolean;
    lines: t.ILineData[];
    person_uuid: string;
    person_name: string;
    plant_uuids: string[];
    handleModalCloseClick: (do_refresh: boolean) => void;
};

type State = {
    competence_titles: string[];
    competences: SingleCompetence[];
    competences_original: SingleCompetence[];
    groups: SingleCompetenceGroup[];
    selected_group: SingleCompetenceGroup;
    selected_competences: SingleCompetence[];
    error: string;
    is_saving: boolean;
};

/** Utility function to determine group that should be selected by default */
function determineDefaultGroup(competences: SingleCompetence[]): string | null {
    // try to be smart when selecting default group - use one from existing competences
    const existing_group_uuids = competences.filter(x => x.level > 0).map(x => x.group_uuid);
    if (existing_group_uuids.length > 0) {
        return existing_group_uuids[0];
    }

    return null;
}

/**
 * Display editable list of competences.
 */
class PeopleCompetenceModal extends Component<Props, State> {
    constructor(props: Props) {
        super(props);

        const groups: SingleCompetenceGroup[] = (Bl.getPlantsForUser() as t.IPlantData[]).map(x => ({
            title: x.title,
            uuid: x.uuid,
        }));

        this.state = {
            competences: [],
            competences_original: [],
            competence_titles: [
                translate("Manufacturing.People.competence_level.0", "None"),
                translate("Manufacturing.People.competence_level.1", "Learning"),
                translate("Manufacturing.People.competence_level.2", "Capable"),
                translate("Manufacturing.People.competence_level.3", "Independent"),
            ],
            groups,
            selected_competences: [],
            selected_group: groups[0],
            error: "",
            is_saving: false,
        };
    }

    async componentDidMount() {
        if (this.props.show) {
            await this.reloadData();
            this.setSelectedGroup(this.state.selected_group.uuid);
        }
    }

    async componentDidUpdate(prevProps: Props) {
        if (prevProps.person_uuid !== this.props.person_uuid || prevProps.show !== this.props.show) {
            await this.reloadData();
            this.setSelectedGroup(this.state.selected_group.uuid);
        }
    }

    async reloadData() {
        const person_uuid = this.props.person_uuid;

        const line_to_line_group_map = new Map<string, t.ILineGroupData>();
        const user_line_groups = Bl.getLineGroupsForUser();
        for (const line_group of user_line_groups) {
            for (const lu of line_group.line_uuids) {
                line_to_line_group_map.set(lu, line_group);
            }
        }

        // set initial competences to all lines to 0
        const competences: SingleCompetence[] = [];
        for (const line of this.props.lines) {
            if (IGNORE_COMPETENCE_FLAG.includes(line.tags[LINE_TAGS.type])) {
                continue;
            }

            const line_group = line_to_line_group_map.get(line.uuid);
            if (!line_group) {
                continue;
            }

            competences.push({
                group_uuid: line.plant_uuid,
                level: -1, // -1 indicates None but also not-set-in-backend
                title: line.title,
                uuid: line.uuid,
                line_group_title: line_group.title,
                weight: line_group.weight * 1000000 + line.weight,
            });
        }

        // Sort by line-group weight
        competences.sort((l1, l2) => l1.weight - l2.weight);

        // Now get data from server to add into initial levels
        let error = "";
        try {
            const data = await getBackend().manufacturing.getLineCompetence({ person_uuid });
            const line_uuid_to_competence_map: Map<string, SingleCompetence> = new Map(
                competences.map(competence => [competence.uuid, competence])
            );
            for (const competence of data.competences) {
                const initial_competence = line_uuid_to_competence_map.get(competence.line_uuid);
                if (initial_competence !== undefined) {
                    initial_competence.level = competence.level;
                }
            }
        } catch (e) {
            error = (e as Error).message;
        }

        const competences_original = deepClone(competences);

        competences.forEach(x => {
            x.level = Math.max(x.level, 0);
        });

        let selected_group_uuid = determineDefaultGroup(competences);
        let selected_group = this.state.selected_group;
        if (selected_group_uuid !== null) {
            selected_group = this.state.groups.filter(x => x.uuid === selected_group_uuid)[0];
            selected_group_uuid = selected_group.uuid;
        }
        const selected_competences = competences.filter(x => x.group_uuid === selected_group_uuid);
        this.setState({
            competences,
            competences_original,
            selected_competences,
            selected_group,
            error,
            is_saving: false,
        });
    }

    handleCompetenceClick(item_uuid: string, level: number) {
        this.setState(prev_state => {
            prev_state.competences.forEach(item => {
                if (item.uuid === item_uuid) {
                    item.level = level;
                }
            });
            prev_state.selected_competences.forEach(item => {
                if (item.uuid === item_uuid) {
                    item.level = level;
                }
            });
            return prev_state;
        });
    }

    setSelectedGroup(group_uuid: string) {
        const hits = this.state.groups.filter(x => x.uuid === group_uuid);
        let selected_group = this.state.groups[0];
        if (hits.length > 0) {
            selected_group = hits[0];
        }

        this.setState({
            selected_group,
            selected_competences: this.state.competences.filter(x => x.group_uuid === selected_group.uuid),
        });
    }

    async save() {
        this.setState({ is_saving: true });
        try {
            const api = getBackend().manufacturing;
            for (const new_comp of this.state.competences) {
                const old = this.state.competences_original.filter(x => x.uuid === new_comp.uuid);
                if (old.length > 0) {
                    // Already stored in backend
                    if (new_comp.level === 0 && old[0].level < 0) {
                        // Do nothing - not set in backend and still is None
                    } else if (new_comp.level === 0) {
                        // Remove from backend
                        await api.deleteLineCompetence({
                            line_uuid: new_comp.uuid,
                            person_uuid: this.props.person_uuid,
                        });
                    } else if (new_comp.level !== old[0].level) {
                        // Update in backend
                        await api.upsertLineCompetence({
                            level: new_comp.level,
                            line_uuid: new_comp.uuid,
                            person_uuid: this.props.person_uuid,
                        });
                    }
                } else {
                    if (new_comp.level > 0) {
                        // Add to backend
                        await api.upsertLineCompetence({
                            level: new_comp.level,
                            line_uuid: new_comp.uuid,
                            person_uuid: this.props.person_uuid,
                        });
                    }
                }
            }

            this.setState({ is_saving: false });
            this.props.handleModalCloseClick(true);
        } catch (e) {
            this.setState({
                error: (e as Error).message,
                is_saving: false,
            });
        }
    }

    render() {
        const { person_name, handleModalCloseClick } = this.props;
        const select_options = this.state.groups.map(x => ({ value: x.uuid, label: x.title }));
        const select_current = select_options.filter(x => x.value === this.state.selected_group.uuid)[0];

        return (
            <Modal
                show={this.props.show}
                size="lg"
                dialogClassName="people-competences-modal modal-dialog-scrollable modal-max"
                onHide={() => handleModalCloseClick(false)}
            >
                <Modal.Header>
                    <Modal.Title>
                        <FormattedMessage
                            id="Manufacturing.People.edit_competences"
                            defaultMessage="Edit competences"
                        />
                        {person_name && (
                            <>
                                {" - "}
                                <strong>{person_name}</strong>
                            </>
                        )}
                    </Modal.Title>
                    <button type="button" className="close" onClick={() => handleModalCloseClick(true)}>
                        <span aria-hidden="true">×</span>
                        <span className="sr-only">
                            <FormattedMessage id="common.close" defaultMessage="Close" />
                        </span>
                    </button>
                </Modal.Header>
                <Toolbar className="people-competences-modal__toolbar">
                    <ToolbarItem className="flex-grow-1">
                        <Select
                            classNamePrefix="react-select"
                            className="react-select flex-grow-1"
                            options={select_options}
                            value={select_current}
                            onChange={event => {
                                this.setSelectedGroup(event.value);
                            }}
                            placeholder="Select group"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                                menuPortal: base => ({
                                    ...base,
                                    zIndex: 9999,
                                }),
                            }}
                        />
                    </ToolbarItem>
                </Toolbar>
                <Modal.Body className="p-0">
                    <ul className="list-group list-group-flush list-group-edit-competences">
                        {this.state.selected_competences.map(selected_competence => (
                            <li
                                key={selected_competence.uuid}
                                className="list-group-item d-flex align-items-center flex-wrap"
                            >
                                <span>
                                    {selected_competence.title} ({selected_competence.line_group_title})
                                </span>
                                <span className="badge-list ml-auto">
                                    {this.state.competence_titles.map((competence_title, level) => {
                                        const isLevelSelected = selected_competence.level === level;
                                        return (
                                            <button
                                                className={classNames("btn badge badge-competence", {
                                                    [`badge-competence-${level}`]: isLevelSelected,
                                                })}
                                                onClick={() => {
                                                    this.handleCompetenceClick(selected_competence.uuid, level);
                                                }}
                                                key={level}
                                            >
                                                {competence_title}
                                            </button>
                                        );
                                    })}
                                </span>
                            </li>
                        ))}
                    </ul>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className="btn btn-primary"
                        disabled={this.state.is_saving}
                        onClick={() => {
                            this.save();
                        }}
                    >
                        <FormattedMessage id="common.save" defaultMessage="Save" />
                    </button>
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() => {
                            handleModalCloseClick(false);
                        }}
                    >
                        <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                    </button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default PeopleCompetenceModal;
